import type { ComponentProps, FC, ReactNode } from 'react';
import { Slot } from 'radix-ui';
import { cn } from '../lib/utils';

/**
 * Row chrome shared with `DropdownMenuItem`: same height, radius, type, and
 * highlight. `aria-current="page"` marks the active destination.
 */
const listItemClassName =
  "flex min-h-9 w-full items-center gap-2 rounded-md px-2 py-1.5 [font-size:var(--text-body)] [line-height:var(--text-body--line-height)] outline-hidden select-none aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

/** Only an `asChild` row (a link or button) is interactive, so only it gets hover and focus chrome. */
const interactiveClassName =
  'cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground aria-[current=page]:hover:bg-primary';

interface ListItemProps {
  /** Render the child element as the row, so a link can be the list item. */
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const ListItem: FC<
  ListItemProps & Omit<ComponentProps<'li'>, 'children' | 'className'>
> = ({ asChild = false, children, className, ...props }) => {
  if (asChild) {
    return (
      <li>
        <Slot.Root
          className={cn(listItemClassName, interactiveClassName, className)}
          {...props}
        >
          {children}
        </Slot.Root>
      </li>
    );
  }

  return (
    <li className={cn(listItemClassName, className)} {...props}>
      {children}
    </li>
  );
};
